import { InputAdornment, Stack, SxProps, TextField, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import VectorImage, { type VectorImageName } from '~components/media/vectorImage';

import Dimens from '../../themes/dimens';

interface Props {
	onChange: (value: string) => void;
	value?: string;
	placeholder?: string;
	label?: string;
	prefix?: string;
	inputType?: string;
	sx?: SxProps;
	readonly?: boolean;
	disabled?: boolean;
	autofocus?: boolean;
	required?: boolean;
	copyable?: boolean;
	hasError?: boolean;
	errorText?: string;
	onClick?: () => void;
	StartIcon?: VectorImageName;
	EndIcon?: VectorImageName;
}

const InputField = ({
	value,
	label,
	readonly = false,
	disabled = false,
	inputType = 'text',
	autofocus = false,
	required = false,
	copyable = false,
	hasError = false,
	errorText,
	prefix,
	onClick,
	StartIcon,
	EndIcon,
	...props
}: Props) => {
	const { palette, typography } = useTheme();
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const borderColor =
		hasError ? palette.borderError.main
		: disabled ? palette.borderDisabled.main
		: palette.borderPrimary.main;
	const hoverBorderColor = disabled ? palette.borderDisabled.main : palette.borderPrimarySolid.main;
	const backgroundColor = disabled ? palette.bgDisabled.main : palette.bgPrimary.main;

	const copyContent = () => {
		navigator.clipboard.writeText(value ?? '');
		enqueueSnackbar(t('copiedToClipboard', { ns: 'system' }), { variant: 'info' });
	};

	const startsWithPrefix = (prefix && value?.startsWith(prefix)) === true;

	return (
		<Stack sx={{ display: 'flex', gap: Dimens.spacingMd }}>
			{label && (
				<Typography variant='labelSm' color='textSecondary'>
					{label}
					{required && (
						<Typography variant='labelSm' color='textBrandTertiary' component='span'>
							*
						</Typography>
					)}
				</Typography>
			)}
			<TextField
				onClick={onClick}
				placeholder={props.placeholder}
				slotProps={{
					input: {
						readOnly: readonly,
						type: inputType,
						autoFocus: autofocus,
						startAdornment:
							prefix ?
								<InputAdornment
									position='start'
									sx={{
										background: palette.bgDisabled.main,
										paddingRight: Dimens.spacingMd,
										borderRight: `1px solid ${
											disabled ? palette.borderDisabled.main : palette.borderPrimary.main
										}`,
										maxHeight: 'unset',
									}}
								>
									<Typography
										variant='paragraph'
										color='textDisabled'
										sx={{
											userSelect: 'none',
											paddingLeft: Dimens.spacingLg,
										}}
									>
										{prefix}
									</Typography>
								</InputAdornment>
							:	StartIcon && (
									<InputAdornment
										position='start'
										sx={{
											paddingLeft: Dimens.spacingLg,
											maxHeight: 'unset',
										}}
									>
										<VectorImage
											name={StartIcon}
											height={Dimens.spacing2xl}
											width={Dimens.spacing2xl}
										/>
									</InputAdornment>
								),
						endAdornment:
							copyable ?
								<InputAdornment position='end'>
									<VectorImage
										name='icon/copy'
										height={Dimens.spacing2xl}
										width={Dimens.spacing2xl}
										onClick={copyContent}
									/>
								</InputAdornment>
							:	EndIcon && (
									<InputAdornment position='end' sx={{}}>
										<VectorImage
											name={EndIcon}
											height={Dimens.spacing2xl}
											width={Dimens.spacing2xl}
										/>
									</InputAdornment>
								),
					},
				}}
				disabled={disabled}
				value={value}
				sx={{
					...props.sx,
					height: '100%',
					outline: 'none',
					backgroundColor: backgroundColor,
					borderRadius: Dimens.radiusMd,
					backgroundClip: 'border-box',
					overflow: 'hidden',
					input: {
						...typography.paragraph,
						color: palette.textTertiary.main,
						backgroundColor: backgroundColor,
						padding: `${Dimens.spacingMd} ${Dimens.spacingLg} ${Dimens.spacingMd} ${StartIcon ? Dimens.spacingMd : Dimens.spacingLg}`,
						minHeight: '24px',
						'&::placeholder': {
							color: palette.textPlaceholder.main,
							opacity: 1,
						},
					},
					'& .MuiInputAdornment-root': {
						margin: 0,
					},
					'& .MuiInputBase-adornedStart': {
						paddingLeft: 0,
						alignItems: 'unset',
					},
					'& .MuiInputBase-adornedEnd': {
						cursor: copyable ? 'pointer' : 'default',
					},
					'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
						border: `2px solid ${hoverBorderColor}` + '!important',
						borderColor: hoverBorderColor + '!important',
						outline: 'none' + '!important',
					},
					'&:hover .MuiOutlinedInput-notchedOutline': {
						border: `2px solid ${hoverBorderColor}` + '!important',
						borderColor: hoverBorderColor + '!important',
						outline: 'none' + '!important',
					},
					'&:focus .MuiOutlinedInput-notchedOutline': {
						border: `2px solid ${hoverBorderColor}` + '!important',
						borderColor: hoverBorderColor + '!important',
						outline: 'none' + '!important',
					},
					'&:active .MuiOutlinedInput-notchedOutline': {
						border: `2px solid ${hoverBorderColor}` + '!important',
						borderColor: hoverBorderColor + '!important',
						outline: 'none' + '!important',
					},
					'&:focus-visible .MuiOutlinedInput-notchedOutline': {
						border: `2px solid ${hoverBorderColor}` + '!important',
						borderColor: hoverBorderColor + '!important',
						outline: 'none' + '!important',
					},
					'& fieldset': {
						border: `1px solid ${borderColor}` + '!important',
						borderColor: borderColor + '!important',
						outline: 'none' + '!important',
						top: 0,
						'& legend': {
							display: 'none',
						},
					},
				}}
				error={hasError || startsWithPrefix}
				helperText={
					startsWithPrefix ?
						t('cantContain', {
							ns: 'errors',
							value: prefix,
							interpolation: {
								escapeValue: false,
							},
						})
					: hasError ?
						errorText
					:	''
				}
				onChange={(e: { target: { value: string } }) => props.onChange(e.target.value)}
			/>
		</Stack>
	);
};

export default InputField;
